import { useEffect, useState } from "react";
import { SubsubscriberService } from "@root/services/sub-subscriber";
import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";

export const useSubSubscriberManagement = () => {
  const [subsubscriberlLoading, setSubsubscriberLoading] = useState(false);
  const [deleteSubsubscriberlLoading, setDeleteSubsubscriberlLoading] =
    useState(false);
  const [addSubsubscriberlLoading, setAddSubsubscriberLoading] =
    useState(false);
  const [subsubscriberData, setSubsubscriberData] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);

  const subSubscriberService = new SubsubscriberService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  // The backend returns an already-translated message (e.g. "이미 사용 중인
  // 이메일입니다.") — prefer it over the generic fallback text.
  const getServerErrorMessage = (error: unknown): string | undefined =>
    (error as any)?.response?.data?.message;

  // 🔹 Fetch
  const fetchSubsubscriber = async (payload: any) => {
    setSubsubscriberLoading(true);
    try {
      const res = await subSubscriberService.fetchSubSubscriber(payload);
      setPageCount(res?.data?.data?.count);
      setSubsubscriberData(res?.data?.data?.rows);
    } catch (error: unknown) {
      console.warn(error);
      showNotification({
        message: t("subsubscriberManagement.errorFetchingDescription"),
        type: "error",
      });
    } finally {
      setSubsubscriberLoading(false);
    }
  };

  // 🔹 Add
  const addSubsubscriber = async (payload: any) => {
    setAddSubsubscriberLoading(true);
    try {
      const res = await subSubscriberService.addSubSubscriber(payload);
      showNotification({
        message: t("subsubscriberManagement.addSuccessDescription"),
        type: "success",
      });
      return res;
    } catch (error: unknown) {
      console.warn(error);
      showNotification({
        message:
          getServerErrorMessage(error) ||
          t("subsubscriberManagement.addErrorDescription"),
        type: "error",
      });
      throw error;
    } finally {
      setAddSubsubscriberLoading(false);
    }
  };

  // 🔹 Delete
  const deleteSubsubscriber = async (id: any) => {
    setDeleteSubsubscriberlLoading(true);
    try {
      const res = await subSubscriberService.deleteSubscriber(id);
      showNotification({
        message: t("subsubscriberManagement.deleteSuccessTitle"),
        type: "success",
      });
      return res;
    } catch (error: unknown) {
      console.warn(error);
      showNotification({
        message:
          getServerErrorMessage(error) ||
          t("subsubscriberManagement.deleteErrorDescription"),
        type: "error",
      });
    } finally {
      setDeleteSubsubscriberlLoading(false);
    }
  };

  return {
    fetchSubsubscriber,
    addSubsubscriber,
    deleteSubsubscriber,
    subsubscriberlLoading,
    addSubsubscriberlLoading,
    deleteSubsubscriberlLoading,
    subsubscriberData,
    page,
    pageCount,
    setPage,
  };
};
